import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, MapPin, Briefcase } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FreelancerProfile = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      const [userRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`),
        axios.get(`${API}/reviews/user/${userId}`),
      ]);
      setUser(userRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Failed to fetch profile', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex items-start gap-6">
              <div className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-4xl font-heading font-bold">
                {user?.full_name?.charAt(0)}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-heading font-bold text-primary mb-2" data-testid="profile-name">
                  {user?.full_name}
                </h1>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 fill-amber-500 text-amber-500" />
                    <span className="font-heading font-semibold">{user?.rating?.toFixed(1) || '0.0'}</span>
                    <span className="text-sm text-muted-foreground">({user?.total_reviews || 0} reviews)</span>
                  </div>
                  <Badge variant="secondary" className="capitalize">{user?.role}</Badge>
                  {user?.verified && <Badge variant="outline">Verified</Badge>}
                </div>
                <p className="text-muted-foreground mb-4" data-testid="profile-bio">{user?.bio || 'No bio provided'}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Briefcase className="w-4 h-4" />
                  <span>{user?.completed_jobs || 0} jobs completed</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {user?.skills && user.skills.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-heading">Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap">
                {user.skills.map((skill, idx) => (
                  <Badge key={idx} variant="secondary">{skill}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {reviews.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-heading">Reviews ({reviews.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b pb-4 last:border-0" data-testid={`review-${review.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-amber-500 text-amber-500' : 'text-slate-200'}`} />
                        ))}
                      </div>
                      <span className="text-sm font-medium">{review.reviewer_name}</span>
                      <span className="text-xs text-muted-foreground">{new Date(review.created_at).toLocaleDateString('en-IN')}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{review.comment}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FreelancerProfile;
